// This file is kept for any shared types across the Library

import type { IconType } from '@kognitos/lattice';
import type {
  V1Asset,
  V1CollectionField,
  V1CollectionStage,
  V1ComparisonOperator,
  V1Folder,
  V1ValueScalarType,
} from '@/generated/hooks/bishop/models';
import type { V1AutomationCollectionUsage } from '@/generated/hooks/grimoire/models';

/**
 * Workspace library variant
 */
export enum EWorkspaceLibraryVariant {
  ASSETS = 'ASSETS',
  COLLECTIONS = 'COLLECTIONS',
}

/**
 * Assets view mode
 */
export type TAssetsViewMode = 'grid' | 'list';

/**
 * Org/workspace scope of the page viewing an asset. Used to resolve an
 * asset's scope-free `reference_uri` into a fetchable AIP resource name.
 */
export type TAssetScope = {
  organization: string;
  workspace: string;
};

// Wire types from bishop are reused directly. We layer on:
//   1. a `kind` discriminant so heterogeneous lists narrow correctly
//   2. UI-only fields the API doesn't return yet
export type IAssetView = V1Asset & {
  kind: 'asset';
  // UI-only — TODO: drop once bishop exposes thumbnails
  preview_url?: string;
};

export type IFolderView = V1Folder & {
  kind: 'folder';
};

export type ILibraryItem = IAssetView | IFolderView;

/**
 * Upload status for assets
 */
export type TUploadStatus = 'uploading' | 'complete' | 'error';

/**
 * Upload shaped for assets
 */
export interface IUpload {
  /** Client-generated stable id for this upload row. */
  id: string;
  /** The local File the user picked or dropped. */
  file: File;
  /** Lifecycle of the underlying UploadFile call. */
  status: TUploadStatus;
  /** Bishop file resource name returned by UploadFile; needed as `file_name` for CreateAsset. */
  resourceName?: string;
}

/**
 * Configuration for an asset action menu item.
 */
export type TAssetActionItem = {
  /** Stable menu item identifier. */
  key: string;
  /** Lattice icon type displayed before the label. */
  icon: IconType;
  /** Translated menu label. */
  label: string;
  /** Optional click handler for the menu action. */
  onClick?: () => void;
  /** When true, the item is shown but non-interactive. */
  disabled?: boolean;
};

/**
 * Supported library detail item variants.
 */
export type TLibraryDetailItemVariant =
  | 'text'
  | 'textarea'
  | 'tags'
  | 'code'
  | 'date'
  | 'datetime'
  | 'select'
  | 'file'
  | 'checkbox'
  | 'radio'
  | 'switch';

/**
 * Supported library detail item render states.
 */
export type TLibraryDetailItemState = 'default' | 'editable';

/**
 * Upload lifecycle of a file chosen in the `file` detail item variant.
 */
export type TLibraryDetailItemFileState = 'ready' | 'uploading' | 'error';

/**
 * Option used by editable select, checkbox, and radio variants.
 */
export type TLibraryDetailItemOption = {
  /** Stable option value. */
  value: string;
  /** User-facing option label. */
  label: string;
  /** Optional supporting option description. */
  description?: string;
  /** Icon rendered before the label in the select variant. */
  icon?: IconType;
};

/**
 * Asset details panel variants.
 */
export type TAssetDetailsPanelVariant = 'default' | 'folder';

export type AssetTypeFilter =
  | 'folders'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'image'
  | 'json';

export type TSortDirection = 'asc' | 'desc';
export type TSortField = 'name' | 'modified' | 'created';

export interface ISortFieldOption<TField extends string> {
  value: TField;
  label: string;
}

export type AssetSortField = TSortField;
export type AssetSortDirection = TSortDirection;

export type CollectionSortField = TSortField;
export type CollectionSortDirection = TSortDirection;

export interface IFolderCrumb {
  id: string;
  displayName: string;
}

/**
 * Data displayed by the asset details panel.
 */
export interface IAssetDetailsPanelData {
  /** Asset or folder display name. */
  name: string;
  /** Optional asset description. */
  description?: string;
  /** Asset or folder tags. */
  tags?: string[];
  /** Display names of automations that reference the asset. */
  automationUsage?: string[];
  /** File or folder type label. */
  type?: string;
  /** Human-readable size label. */
  size?: string;
  /** Location label. */
  location?: string;
  /** Creator display name. */
  createdBy?: string;
  /** Creation timestamp display label. */
  createdAt?: string;
  /** Modified timestamp display label. */
  modified?: string;
  /** Reference URL or resource name used by automations. */
  reference?: string;
  /** Preview thumbnail URL for assets. */
  previewUrl?: string;
}

/**
 * What `useAssetThumbnail` needs to build a preview for one asset.
 */
export interface IAssetThumbnailSource {
  /** Bishop file resource name. */
  fileName?: string;
  mimeType?: string;
  sizeBytes?: string;
  /** Content version — the asset's `update_time`. */
  version?: string;
}

/**
 * Metadata field labels and values shown in the lower panel section.
 */
export type TFileDetail = {
  /** User-facing label. */
  label: string;
  /** User-facing value. */
  value?: string;
};

/**
 * Collection stage as carried in the collection page's `?stage=` URL param.
 */
export type TCollectionStageParam = 'draft' | 'published';

/**
 * Fields changed in a collection's draft schema relative to its published
 * schema, as shown in the publish confirmation dialog.
 */
export interface ISchemaFieldChanges {
  added: V1CollectionField[];
  edited: V1CollectionField[];
}

/**
 * Collection template data.
 */
export interface ICollectionTemplate {
  id: string;
  nameKey: string;
  descriptionKey: string;
  icon: IconType;
}

/**
 * Collection details panel data.
 */
export interface ICollectionDetailsPanelData {
  name: string;
  description?: string;
  stage?: V1CollectionStage;
  automationUsage?: string[];
  fieldCount?: number;
  recordCount?: number;
  createdBy?: string;
  createdAt?: string;
  modified?: string;
  reference?: string;
}

export type TCollectionFieldPanelMode = 'add' | 'edit';

export interface ICollectionFieldFormValues {
  name: string;
  description: string;
  type: V1ValueScalarType;
  required: boolean;
  unique: boolean;
  defaultValue: string;
}

// Record filters are `bishop.files.v1.RowFilter`; re-exported here so the filter
// builder keeps one import for both the proto shapes and the UI state below.
export type {
  V1Comparison,
  V1FuzzyMatch,
  V1LogicalGroup,
  V1RowFilter,
  V1SortOrder,
} from '@/generated/hooks/bishop/models';
export type {
  V1ComparisonOperator,
  V1LogicalOperator,
} from '@/generated/hooks/bishop/models';

/**
 * Filter builder operators that aren't bishop `V1ComparisonOperator`s.
 * `FuzzyMatch` compiles to a `RowFilter.fuzzy_match` node; `NotHas`/`NotIn`
 * compile to a `RowFilter.negation` wrapping `Has`/`In`; `Between` compiles to
 * a `RowFilter.logical` AND of `GreaterThanOrEqual` and `LessThanOrEqual`.
 */
export const ERecordFilterOperator = {
  FuzzyMatch: 'FUZZY_MATCH',
  NotHas: 'COMPARISON_OPERATOR_NOT_HAS',
  NotIn: 'COMPARISON_OPERATOR_NOT_IN',
  Between: 'BETWEEN',
} as const;

export type TRecordFilterSyntheticOperator =
  (typeof ERecordFilterOperator)[keyof typeof ERecordFilterOperator];

/**
 * Every operator the records filter builder offers. UNSPECIFIED is dropped
 * from bishop's own set — it's never something the user can pick.
 */
export type TRowFilterOperator =
  | Exclude<V1ComparisonOperator, 'COMPARISON_OPERATOR_UNSPECIFIED'>
  | TRecordFilterSyntheticOperator;

/**
 * The editable part of a filter row. `value` stays a string while the user types;
 * `buildRowFilter` encodes it per the field's scalar type. A date is held as
 * `YYYY-MM-DD`, and a `Between` range as an ISO interval, `YYYY-MM-DD/YYYY-MM-DD`.
 */
export interface IRecordFilterDraft {
  field: string;
  operator: TRowFilterOperator;
  value: string;
}

/**
 * One row in the filter builder. The row's own fields are what the inputs show;
 * `applied` is the snapshot the user confirmed, and the only thing that reaches
 * bishop.
 */
export interface IRecordFilterCondition extends IRecordFilterDraft {
  id: string;
  applied?: IRecordFilterDraft;
}

/**
 * How a row stands against its snapshot: `draft` has never been saved, `dirty` has
 * unsaved edits, `applied` matches what bishop is filtering on.
 */
export type TRecordFilterRowState = 'draft' | 'dirty' | 'applied';

/**
 * What a filter row's value holds: one value, a comma-separated list (`IN`), or
 * a `from/to` range (`Between`).
 */
export type TRecordFilterValueShape = 'single' | 'list' | 'range';

/** Automations using one collection, with the state of the page query that
 * fetched it. `automations` is undefined until that query succeeds.
 */
export interface ICollectionUsage {
  automations?: V1AutomationCollectionUsage[];
  isLoading: boolean;
  isError: boolean;
}
